// Telemetry for the framework-detect fall-through (backlog 462).
//
// When `cloudgrid plug` auto-init sniffs a directory and the detector lands on
// the `node` catch-all (no specific framework signal matched), the CLI emits a
// structured, privacy-safe telemetry event so the team gets immediate signal
// about stacks the detector can't yet place. The cockpit's CLI telemetry tab
// filters on `error_code = FRAMEWORK_DETECT_UNKNOWN` (cockpit work is tracked
// separately as W2).
//
// Privacy guardrails (backlog 462 §"Privacy guardrails"):
//   - File and directory NAMES only, at the directory root — never contents.
//   - package.json: dependency / devDependency / script KEYS only — no
//     versions, no author/description/other fields.
//   - No file sizes, no mtimes. No lockfile contents — only which kind exists.
//
// The builder is pure (reads the filesystem, returns a payload) so tests can
// assert the exact shape and prove the privacy invariants. The emitter wraps it
// in `recordCliEvent`, which is best-effort and already swallows every failure.

import { existsSync, readFileSync, readdirSync } from 'node:fs';
import { join } from 'node:path';

import type { SubmitCliEventInput } from '../api/v2-client.js';
import { recordCliEvent } from './telemetry.js';

/** Error code shared with the server / cockpit filter (backlog 462). */
export const FRAMEWORK_DETECT_UNKNOWN = 'FRAMEWORK_DETECT_UNKNOWN';

/** The package/dependency manager whose lockfile is present at the root. */
export type LockfileKind =
  | 'npm'
  | 'yarn'
  | 'pnpm'
  | 'bun'
  | 'pip'
  | 'poetry'
  | 'requirements'
  | 'none';

/** Lockfile sentinels in priority order (most specific first). */
const LOCKFILES: ReadonlyArray<{ file: string; kind: LockfileKind }> = [
  { file: 'package-lock.json', kind: 'npm' },
  { file: 'yarn.lock', kind: 'yarn' },
  { file: 'pnpm-lock.yaml', kind: 'pnpm' },
  { file: 'bun.lockb', kind: 'bun' },
  { file: 'poetry.lock', kind: 'poetry' },
  { file: 'Pipfile.lock', kind: 'pip' },
  { file: 'requirements.txt', kind: 'requirements' },
];

/** Identify the lockfile kind at the directory root, or `none`. */
export function detectLockfileKind(dir: string): LockfileKind {
  for (const { file, kind } of LOCKFILES) {
    if (existsSync(join(dir, file))) return kind;
  }
  return 'none';
}

/** Cap on the number of root entries reported so a giant directory can't
 *  inflate the payload past the server's metadata size bound. */
const MAX_ROOT_ENTRIES = 200;

/** Read the immediate children of `dir`, split into file names and dir names.
 *  Names only — never contents, sizes, or mtimes. Sorted for a stable
 *  signature across machines; empty on any read error. */
function readRootEntries(dir: string): { files: string[]; dirs: string[] } {
  try {
    const entries = readdirSync(dir, { withFileTypes: true });
    const files: string[] = [];
    const dirs: string[] = [];
    for (const entry of entries) {
      if (entry.isDirectory()) dirs.push(entry.name);
      else files.push(entry.name);
    }
    files.sort();
    dirs.sort();
    return {
      files: files.slice(0, MAX_ROOT_ENTRIES),
      dirs: dirs.slice(0, MAX_ROOT_ENTRIES),
    };
  } catch {
    return { files: [], dirs: [] };
  }
}

/** Parse package.json at `dir`, or null when absent/unreadable. */
function readPackageJson(dir: string): Record<string, unknown> | null {
  const path = join(dir, 'package.json');
  if (!existsSync(path)) return null;
  try {
    const parsed = JSON.parse(readFileSync(path, 'utf-8'));
    return parsed && typeof parsed === 'object' ? parsed : null;
  } catch {
    return null;
  }
}

/** Sorted KEYS of a package.json section (dependencies / devDependencies /
 *  scripts) — never the values, so versions and script bodies never leave the
 *  machine. Empty when the section is absent or malformed. */
function sectionKeys(pkg: Record<string, unknown> | null, field: string): string[] {
  const section = pkg?.[field];
  if (section && typeof section === 'object' && !Array.isArray(section)) {
    return Object.keys(section as Record<string, unknown>).sort();
  }
  return [];
}

/**
 * Build the `FRAMEWORK_DETECT_UNKNOWN` telemetry event for `dir`. Pure: reads
 * the filesystem, returns the payload, prints nothing, submits nothing.
 *
 * `user_set_type` is always null here — auto-init only runs when there is no
 * `cloudgrid.yaml`, so the user never declared a `type:`. `fallback_reason` is
 * `no_signal`: the detector reached its `node` catch-all without matching any
 * specific framework.
 */
export function buildFrameworkDetectUnknownEvent(dir: string): SubmitCliEventInput {
  const { files, dirs } = readRootEntries(dir);
  const pkg = readPackageJson(dir);
  return {
    event_type: 'error',
    command: 'cloudgrid plug',
    error_code: FRAMEWORK_DETECT_UNKNOWN,
    text: 'Framework detection fell through to node; no specific framework signal matched.',
    metadata: {
      detected_type: 'node',
      user_set_type: null,
      fallback_reason: 'no_signal',
      root_files: files,
      root_dirs: dirs,
      package_deps: sectionKeys(pkg, 'dependencies'),
      package_dev_deps: sectionKeys(pkg, 'devDependencies'),
      package_scripts: sectionKeys(pkg, 'scripts'),
      lockfile_kind: detectLockfileKind(dir),
      platform: process.platform,
    },
  };
}

/**
 * Emit the fall-through event for `dir`. Best-effort: `recordCliEvent` returns
 * silently when telemetry is disabled or the user isn't signed in, and
 * swallows every network/timeout/HTTP error. The extra try/catch guards the
 * synchronous builder (a filesystem race) so the caller never sees a throw.
 */
export async function emitFrameworkDetectUnknown(dir: string): Promise<void> {
  try {
    await recordCliEvent(buildFrameworkDetectUnknownEvent(dir));
  } catch {
    // Telemetry is opportunistic — never surface a failure to the plug flow.
  }
}
